import React from 'react';
import { useQuery, useMutation, useQueryClient, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiService } from '../services/api';
import type {
  Filters,
  MetaField,
  EnrichmentConfig,
} from '../types/api';

const queryKeys = {
  products: (tableId: string) => ['products', tableId],
  jobStatus: (jobId: string) => ['job', jobId],
  qualityScore: (tableId: string, enrichmentJobId?: string | null) => ['quality', tableId, enrichmentJobId],
  parseCSV: (fileUri: string) => ['csv', fileUri],
  assistantSuggestions: (tableId: string) => ['suggestions', tableId],
} as const;

export const useApi = () => {
  const queryClient = useQueryClient();
  const api = ApiService.getInstance();

  const useProducts = ({
    accountId,
    collectionName,
    tableId,
    limit,
    offset,
    sortField,
    filters,
    metaFields,
    enrichmentJobId,
    enabled = true,
  }: {
    accountId: string;
    collectionName: string;
    tableId: string;
    limit: number;
    offset: number;
    sortField: string;
    filters: Filters;
    metaFields: MetaField[];
    enrichmentJobId?: string;
    enabled?: boolean;
  }) => {
    return useQuery({
      queryKey: [...queryKeys.products(tableId), { offset, limit, sortField, filters, enrichmentJobId }],
      queryFn: () => api.getProductList({
        accountId,
        collectionName,
        tableId,
        limit,
        offset,
        sortField,
        filters,
        metaFields,
        enrichmentJobId,
      }),
      enabled: tableId ? enabled : false,
      keepPreviousData: false,
    });
  };

  const useJobStatus = (jobId: string, enabled = true) => {
    return useQuery({
      queryKey: queryKeys.jobStatus(jobId),
      queryFn: () => api.getJobStatus(jobId),
      enabled: jobId ? enabled : false,
      refetchInterval: (data) => {
        if (data?.data?.status === 'completed' || data?.data?.status === 'failed') {
          return false;
        }
        return 5000; // Poll every 5 seconds while job is running
      },
    });
  };

  const useQualityScore = (tableId: string, enrichmentJobId: string | undefined = undefined, enabled = true) => {
    return useQuery({
      queryKey: queryKeys.qualityScore(tableId, enrichmentJobId),
      queryFn: () => api.getContentQualityScore({ tableId, enrichmentJobId }),
      enabled: tableId ? enabled : false,
      staleTime: 1000 * 60 * 60, // Consider data fresh for 1 hour
      cacheTime: 1000 * 60 * 60 * 24, // Keep unused data in cache for 24 hours
      refetchOnMount: false, // Don't refetch on component mount
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
    });
  };

  const useParseCSV = (fileUri: string, enabled = true) => {
    return useQuery({
      queryKey: queryKeys.parseCSV(fileUri),
      queryFn: () => api.parseCSVInBucket(fileUri),
      enabled,
    });
  };

  const useAssistantSuggestions = (tableId: string, enabled = true) => {
    return useQuery({
      queryKey: queryKeys.assistantSuggestions(tableId),
      queryFn: () => api.getAssistantSuggestions({ tableId }),
      enabled: tableId ? enabled : false,
    });
  };


  const useRunEnrichment = () => {
    return useMutation({
      mutationFn: ({
        accountId,
        collectionName,
        tableId,
        filter,
        config,
      }: {
        accountId: string;
        collectionName: string;
        tableId: string;
        filter: Filters;
        config: EnrichmentConfig;
      }) => api.runEnrichment({ accountId, collectionName, tableId, filter, config }),
      onSuccess: (_, variables) => {
        // Invalidate products for the specific table
        queryClient.invalidateQueries({ 
          queryKey: queryKeys.products(variables.tableId)
        });
      },
    });
  };

  const useExportToShopify = () => {
    return useMutation({
      mutationFn: ({
        input_table_id,
        enrichment_job_id,
        shopify_store_name,
      }: {
        input_table_id: string;
        enrichment_job_id: string;
        shopify_store_name: string;
      }) => api.exportEnrichmentToShopify({
        input_table_id,
        enrichment_job_id,
        shopify_store_name,
      }),
    });
  };

  const invalidateProducts = (tableId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.products(tableId) });
  };

  const prefetchProducts = async (params: Parameters<typeof api.getProductList>[0]) => {
    await queryClient.prefetchQuery({
      queryKey: [...queryKeys.products(params.tableId), { 
        offset: params.offset, 
        limit: params.limit, 
        sortField: params.sortField, 
        filters: params.filters 
      }],
      queryFn: () => api.getProductList(params),
    });
  };

  return {
    useProducts,
    useJobStatus,
    useQualityScore,
    useParseCSV,
    useAssistantSuggestions,
    
    useRunEnrichment,
    useExportToShopify,
    
    // Utilities
    invalidateProducts,
    prefetchProducts,
  };
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, 
      cacheTime: 1000 * 60 * 30, 
      retry: 2,
      refetchOnWindowFocus: true,
    },
  },
});

export const ApiProvider = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}


